// Review comments on a span of code: an ordinary comment on the lines the
// span covers, whose body opens with the span quoted as code, so GitHub
// shows what it is about, and a hidden marker that changelist reads back to
// tint exactly those characters in the diff:
//
//   > `the quoted span`
//   <!-- changelist:span {"v":1,"side":"RIGHT","start":{"line":4,"col":9},"end":{"line":4,"col":24},"quote":"the quoted span"} -->
//
//   <user's text>
//
// A span over several lines is quoted as a fenced block instead. Columns
// count UTF-16 code units of the line's source text, from 0; `end` is
// exclusive. The marker sits right after the quote, so the composer's
// cursor, at the end of what it is given, is where the comment goes.

export type Side = 'LEFT' | 'RIGHT';
export interface SpanPoint {
  line: number;
  col: number;
}
export interface SpanMarker {
  v: 1;
  side: Side;
  start: SpanPoint;
  end: SpanPoint;
  /** The span's text as it was, lines joined with `\n`. */
  quote: string;
}

/** Longest span the marker keeps, in UTF-16 code units. */
export const SPAN_MAX = 2000;
/** Most lines a span may cover. */
export const SPAN_LINES = 200;
const MARKER_OPEN = '<!-- changelist:span ';
const MAX_LINE = 10_000_000;
const MAX_COL = 1_000_000;

/** The marker comment; its JSON never contains `--` or `>`, so nothing in the quote can end it. */
export function encodeSpanMarker(m: SpanMarker): string {
  const json = JSON.stringify({ v: 1, side: m.side, start: m.start, end: m.end, quote: m.quote }).replace(/-/g, '\\u002d').replace(/>/g, '\\u003e');
  return `${MARKER_OPEN}${json} -->`;
}

const point = (raw: unknown): SpanPoint | null => {
  if (!raw || typeof raw !== 'object') return null;
  const { line, col } = raw as Record<string, unknown>;
  if (!Number.isInteger(line) || !Number.isInteger(col)) return null;
  const l = line as number, c = col as number;
  return l >= 1 && l <= MAX_LINE && c >= 0 && c <= MAX_COL ? { line: l, col: c } : null;
};

/** A well-formed marker, or null: a side, an ordered non-empty span of at most SPAN_LINES lines, and a quote that fits it. */
export function normalizeSpanMarker(raw: unknown): SpanMarker | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.v !== 1 || (o.side !== 'LEFT' && o.side !== 'RIGHT') || typeof o.quote !== 'string') return null;
  const start = point(o.start), end = point(o.end);
  if (!start || !end || !o.quote || o.quote.length > SPAN_MAX) return null;
  if (end.line < start.line || (end.line === start.line && end.col <= start.col) || end.line - start.line >= SPAN_LINES) return null;
  // The quote has as many lines as the span covers.
  if (o.quote.split('\n').length !== end.line - start.line + 1) return null;
  return { v: 1, side: o.side, start, end, quote: o.quote };
}

/** The longest run of backticks in `s`. */
const longestTicks = (s: string) => Math.max(0, ...(s.match(/`+/g) ?? []).map((r) => r.length));

/**
 * The span quoted for GitHub: as inline code in a blockquote, or, over
 * several lines, as a fenced block in one; either way fenced with more
 * backticks than the text has in a row.
 */
export function quoteSpan(quote: string): string {
  const fence = '`'.repeat(longestTicks(quote) + 1);
  if (!quote.includes('\n')) {
    // Inline code drops one space at each end when both are there: pad when the text starts or ends with a backtick or a space.
    const pad = /^[` ]|[` ]$/.test(quote) ? ' ' : '';
    return `> ${fence}${pad}${quote}${pad}${fence}`;
  }
  const block = fence.length < 3 ? '```' : fence;
  return [`> ${block}`, ...quote.split('\n').map((l) => (l ? `> ${l}` : '>')), `> ${block}`].join('\n');
}

/** What a comment on span `m` starts with: the quote, then the marker, then room for the comment. */
export function buildSpanPrefill(m: SpanMarker): string {
  const n = normalizeSpanMarker(m);
  if (!n) throw new Error('invalid span');
  return `${quoteSpan(n.quote)}\n${encodeSpanMarker(n)}\n\n`;
}

/** The body of a comment on span `m` (none: `text` as it is): its quote and marker, then `text`. */
export function withSpan(m: SpanMarker | null, text: string): string {
  return m ? buildSpanPrefill(m) + text : text;
}

/**
 * A body split into the span it starts with (as `withSpan` writes it) and
 * the comment after; one that doesn't start so is all comment. The
 * composer edits the comment and shows the span apart, so the quote and
 * marker are never typed over.
 */
export function splitSpan(body: string): { span: SpanMarker | null; text: string } {
  // GitHub may hand a body back with CRLF line ends.
  body = body.replace(/\r\n?/g, '\n');
  const m = parseSpanNote(body);
  if (m) {
    const head = buildSpanPrefill(m);
    if (body.startsWith(head)) return { span: m, text: body.slice(head.length) };
    // Saved without the blank line after it (an empty comment, trimmed).
    if (body === head.trimEnd()) return { span: m, text: '' };
  }
  return { span: null, text: body };
}

/** The span marker in a comment body, wherever it is, or null (none, or malformed). */
export function parseSpanNote(body: string | null | undefined): SpanMarker | null {
  if (!body) return null;
  const at = body.indexOf(MARKER_OPEN);
  if (at < 0) return null;
  const m = /^<!-- changelist:span (\{[^\n]*?\}) -->/.exec(body.slice(at));
  if (!m) return null;
  try {
    return normalizeSpanMarker(JSON.parse(m[1]));
  } catch {
    return null;
  }
}

/** Text of lines `from`…`to` of a side, `lineText(n)` being line n's. */
const joined = (lineText: (n: number) => string | undefined, from: number, to: number): string[] | null => {
  const out: string[] = [];
  for (let n = from; n <= to; n++) {
    const t = lineText(n);
    if (t === undefined) return null;
    out.push(t);
  }
  return out;
};

/** The text of span `start`–`end`, from the lines as they are now; null where a line is missing. */
export function spanText(lineText: (n: number) => string | undefined, start: SpanPoint, end: SpanPoint): string | null {
  const lines = joined(lineText, start.line, end.line);
  if (!lines) return null;
  if (lines.length === 1) return lines[0].slice(start.col, end.col);
  return [lines[0].slice(start.col), ...lines.slice(1, -1), lines[lines.length - 1].slice(0, end.col)].join('\n');
}

/**
 * Where marker `m`'s span is in the lines as they are now: where it says,
 * if its quote is still there; else (the code moved) its quote's first
 * place in lines `within` (the thread's lines here, on its side); else null.
 */
export function locateSpan(m: SpanMarker, lineText: (n: number) => string | undefined, within?: { from: number; to: number }): { start: SpanPoint; end: SpanPoint } | null {
  if (spanText(lineText, m.start, m.end) === m.quote) return { start: m.start, end: m.end };
  if (!within || within.to < within.from) return null;
  const lines = joined(lineText, Math.max(1, within.from), within.to);
  if (!lines) return null;
  const from = Math.max(1, within.from);
  const text = lines.join('\n');
  const i = text.indexOf(m.quote);
  if (i < 0) return null;
  const at = (offset: number): SpanPoint => {
    let line = from, rest = offset;
    for (const l of lines) {
      if (rest <= l.length) return { line, col: rest };
      rest -= l.length + 1;
      line++;
    }
    return { line: from + lines.length - 1, col: lines[lines.length - 1].length };
  };
  return { start: at(i), end: at(i + m.quote.length) };
}

/**
 * The marker for a selection from `start` to `end` on `side` (`end`
 * exclusive), with its text from the lines: a selection ending at the start
 * of a line (a triple click) ends at the end of the line before; one that
 * is only whitespace, too long or over too many lines is no span (null).
 */
export function spanFor(side: Side, start: SpanPoint, end: SpanPoint, lineText: (n: number) => string | undefined): SpanMarker | null {
  let e = end;
  if (e.col === 0 && e.line > start.line) {
    const prev = lineText(e.line - 1);
    if (prev === undefined) return null;
    e = { line: e.line - 1, col: prev.length };
  }
  const quote = spanText(lineText, start, e);
  if (quote === null || !quote.trim()) return null;
  return normalizeSpanMarker({ v: 1, side, start, end: e, quote });
}

/** How much of a span's first line its folded summary shows; a one-line span up to SUMMARY_WHOLE characters shows whole. */
export const SUMMARY_CUT = 60;
export const SUMMARY_WHOLE = 80;
/**
 * A span's one-line summary (SpanQuote): where it is (`line 12`,
 * `lines 12–14`, on the old side `old line 12`), whether it folds (more
 * than one line, or a long one), and how much of its first line the folded
 * summary shows (`cut`), with `more` when that leaves some out.
 */
export function spanSummary(m: Pick<SpanMarker, 'side' | 'start' | 'end' | 'quote'>): { where: string; folds: boolean; cut: number; more: boolean } {
  const old = m.side === 'LEFT' ? 'old ' : '';
  const where = m.start.line === m.end.line ? `${old}line ${m.start.line}` : `${old}lines ${m.start.line}–${m.end.line}`;
  const first = m.quote.split('\n')[0];
  const lines = m.quote.includes('\n');
  const folds = lines || first.length > SUMMARY_WHOLE;
  const cut = folds ? Math.min(first.length, SUMMARY_CUT) : first.length;
  return { where, folds, cut, more: lines || cut < first.length };
}

/**
 * GitHub's HTML of a span comment without the quote it starts with (the
 * first blockquote, which `quoteSpan` wrote), for showing the span apart;
 * null when it doesn't start with one (render the Markdown instead). The
 * quote is code, which GitHub escapes, so its end is the first close.
 */
export function withoutSpanQuoteHtml(html: string | null | undefined): string | null {
  if (!html) return null;
  const m = /^\s*<blockquote(?:\s[^>]*)?>[\s\S]*?<\/blockquote>\s*/.exec(html);
  return m ? html.slice(m[0].length) : null;
}
